import { DiscountElementTargetType } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { TARGET_TYPE_LABELS } from './targetTypeLabels';

/**
 * Colored pills for discount element facets, so types are scannable at a
 * glance: one palette per DISCOUNT type (fixed price / percent / amount off)
 * and one per TARGET type (product id / family / group / model / all).
 */

const PILL_BASE =
  'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase whitespace-nowrap';

const DISCOUNT_TYPE_PILLS: Record<
  string,
  { label: string; className: string }
> = {
  FIXED_PRICE: {
    label: 'Fixed Price',
    className: 'border-purple-200 bg-purple-50 text-purple-700',
  },
  PERCENT_BPS_OFF: {
    label: 'Percent',
    className: 'border-blue-200 bg-blue-50 text-blue-700',
  },
  AMOUNT_OFF: {
    label: 'Amount Off',
    className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  },
};

export function DiscountTypePill({ discountType }: { discountType: string }) {
  const cfg = DISCOUNT_TYPE_PILLS[discountType] ?? {
    label: discountType,
    className: 'border-border bg-muted text-muted-foreground',
  };
  return <span className={cn(PILL_BASE, cfg.className)}>{cfg.label}</span>;
}

const TARGET_TYPE_PILLS: Record<DiscountElementTargetType, string> = {
  [DiscountElementTargetType.PRODUCT_VARIANT_ID]:
    'border-slate-200 bg-slate-50 text-slate-700',
  [DiscountElementTargetType.PRODUCT_CATEGORY]:
    'border-amber-200 bg-amber-50 text-amber-700',
  [DiscountElementTargetType.PRODUCT_LINE]:
    'border-teal-200 bg-teal-50 text-teal-700',
  [DiscountElementTargetType.PRODUCT_FAMILY]:
    'border-rose-200 bg-rose-50 text-rose-700',
  [DiscountElementTargetType.PRODUCT_GROUP]:
    'border-indigo-200 bg-indigo-50 text-indigo-700',
  [DiscountElementTargetType.ALL_PRODUCTS]:
    'border-border bg-muted text-muted-foreground',
};

export function TargetTypePill({
  targetType,
}: {
  targetType: DiscountElementTargetType;
}) {
  return (
    <span className={cn(PILL_BASE, TARGET_TYPE_PILLS[targetType])}>
      {TARGET_TYPE_LABELS[targetType]}
    </span>
  );
}
